import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { 
  getPageMeta, 
  getFullTitle, 
  SITE_URL,
  DEFAULT_OG_IMAGE,
  getHreflangLinks,
  ECOSYSTEM_FULL,
  type PageMetaConfig 
} from '@/lib/seoConfig';
import { getCanonicalUrl, getCrossDomainAlternate } from '@/lib/domainConfig';

interface PageMetaProps {
  /** Override default title */
  title?: string;
  /** Override default description */
  description?: string;
  /** Override default keywords */
  keywords?: string;
  /** Custom canonical URL path */
  canonicalPath?: string;
  /** Custom OG image URL */
  ogImage?: string;
  /** Prevent indexing (for auth/private pages) */
  noindex?: boolean;
  /** Additional structured data (JSON-LD) */
  structuredData?: Record<string, unknown>;
}

/**
 * PageMeta Component
 * 
 * Manages SEO meta tags for each page using react-helmet-async.
 * Automatically pulls from seoConfig.ts based on current route,
 * but allows per-page overrides.
 * 
 * @example
 * // Basic usage - uses config from seoConfig.ts
 * <PageMeta />
 * 
 * // With overrides
 * <PageMeta 
 *   title="My Custom Page" 
 *   description="Custom description" 
 * />
 * 
 * // With structured data
 * <PageMeta structuredData={{
 *   "@context": "https://schema.org",
 *   "@type": "Course",
 *   "name": "Acting Fundamentals"
 * }} />
 */
export function PageMeta({
  title: overrideTitle,
  description: overrideDescription,
  keywords: overrideKeywords,
  canonicalPath,
  ogImage,
  noindex: overrideNoindex,
  structuredData,
}: PageMetaProps) {
  const location = useLocation();
  const pageMeta = getPageMeta(location.pathname);
  
  const title = overrideTitle || pageMeta.title;
  const fullTitle = getFullTitle(title);
  const description = overrideDescription || pageMeta.description;
  const keywords = overrideKeywords || pageMeta.keywords;
  const canonical = canonicalPath 
    ? getCanonicalUrl(canonicalPath) 
    : getCanonicalUrl(location.pathname);
  const crossDomainAlternate = getCrossDomainAlternate(location.pathname);
  const image = ogImage || pageMeta.ogImage || DEFAULT_OG_IMAGE;
  const shouldNoindex = overrideNoindex ?? pageMeta.noindex;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonical} />
      
      {/* Hreflang tags for SEO - i18n ready */}
      {getHreflangLinks(canonical).map(({ hreflang, href }) => (
        <link key={hreflang} rel="alternate" hrefLang={hreflang} href={href} />
      ))}
      
      {/* Cross-domain alternate for shared content */}
      {crossDomainAlternate && (
        <link rel="alternate" href={crossDomainAlternate} />
      )}
      
      {/* Robots */}
      {shouldNoindex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={pageMeta.ogType || 'website'} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="CastReady" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}

/**
 * Organization structured data for site-wide use
 */
export const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "CastReady",
  "url": SITE_URL,
  "logo": `${SITE_URL}/icon-512.png`,
  "description": ECOSYSTEM_FULL,
  "knowsAbout": [
    "acting", "casting", "talent representation",
    "film", "television", "commercials", "Broadway",
    "screenwriting", "production crew", "hair and makeup"
  ],
  "sameAs": [
    // Add social media URLs when available
  ],
};

/**
 * WebSite schema with SearchAction (enables sitelinks search box in Google).
 */
export const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "CastReady",
  "url": SITE_URL,
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": `${SITE_URL}/search?q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
};

/**
 * Combined homepage @graph: Organization + WebSite.
 * Use as `structuredData` on the homepage for richer Google entity recognition.
 */
export const HOMEPAGE_GRAPH = {
  "@context": "https://schema.org",
  "@graph": [ORGANIZATION_SCHEMA, WEBSITE_SCHEMA],
};

/**
 * OfferCatalog schema helper — used on the Pricing page so each plan
 * surfaces with price + currency in rich results.
 */
export const createOfferCatalogSchema = (
  catalogName: string,
  offers: Array<{ name: string; description?: string; priceCents: number; currency?: string; url?: string }>,
) => ({
  "@context": "https://schema.org",
  "@type": "OfferCatalog",
  "name": catalogName,
  "itemListElement": offers.map((o, i) => ({
    "@type": "Offer",
    "position": i + 1,
    "name": o.name,
    "description": o.description,
    "price": (o.priceCents / 100).toFixed(2),
    "priceCurrency": o.currency || "USD",
    "availability": "https://schema.org/InStock",
    "url": o.url ? `${SITE_URL}${o.url}` : undefined,
  })),
});

/**
 * BreadcrumbList schema helper
 */
export const createBreadcrumbSchema = (items: { name: string; path: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": `${SITE_URL}${item.path}`,
  })),
});

/**
 * FAQPage schema helper
 */
export const createFAQSchema = (faqs: { question: string; answer: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer,
    },
  })),
});

/**
 * Course structured data helper
 */
export const createCourseSchema = (course: {
  name: string;
  description: string;
  provider?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Course",
  "name": course.name,
  "description": course.description,
  "provider": {
    "@type": "Organization",
    "name": course.provider || "CastReady",
    "sameAs": SITE_URL,
  },
});

/**
 * SoftwareApplication schema helper
 */
export const createSoftwareSchema = (app: {
  name: string;
  description: string;
  applicationCategory?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": app.name,
  "description": app.description,
  "applicationCategory": app.applicationCategory || "BusinessApplication",
  "operatingSystem": "Web",
  "offers": {
    "@type": "Offer",
    "price": "12.00",
    "priceCurrency": "USD",
  },
});

/**
 * Article structured data helper for blog posts
 */
export const createArticleSchema = (article: {
  headline: string;
  description: string;
  author: string;
  datePublished: string;
  dateModified?: string;
  image?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": article.headline,
  "description": article.description,
  "author": {
    "@type": "Person",
    "name": article.author,
  },
  "publisher": {
    "@type": "Organization",
    "name": "CastReady",
    "logo": {
      "@type": "ImageObject",
      "url": `${SITE_URL}/icon-512.png`,
    },
  },
  "datePublished": article.datePublished,
  "dateModified": article.dateModified || article.datePublished,
  "image": article.image ? `${SITE_URL}${article.image}` : DEFAULT_OG_IMAGE,
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": SITE_URL,
  },
});

/**
 * WebPage schema helper — lightweight per-page entity for legal,
 * trust, security, and accessibility pages. Adds `about: Organization`
 * so search engines can tie the page to the CastReady entity.
 */
export const createWebPageSchema = (page: {
  name: string;
  description: string;
  path: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": page.name,
  "description": page.description,
  "url": `${SITE_URL}${page.path}`,
  "isPartOf": {
    "@type": "WebSite",
    "name": "CastReady",
    "url": SITE_URL,
  },
  "about": ORGANIZATION_SCHEMA,
});

export default PageMeta;
